/**
 * WritingSection — "My Writing", a small author-portfolio preview of public
 * manuscripts only. Reuses StoryCard from the Studio feature so a story
 * looks identical whether seen there or here. Private drafts/unlisted
 * stories are filtered out before this component ever sees them.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { SectionHeader } from '@/components/site/primitives';
import { StoryCard } from '@/components/myWriting';
import { stagger, viewport } from '@/lib/motion';
import EmptyState from './EmptyState';
import { PROFILE } from '@/constants/testIds';

export default function WritingSection({ stories, limit }) {
  const visible = limit ? stories.slice(0, limit) : stories;

  return (
    <section className="u-container mt-16 md:mt-24" data-testid={PROFILE.writing}>
      <div className="flex items-end justify-between gap-4">
        <SectionHeader eyebrow="From the studio" title="My Writing" />
        <Link to="/app/studio" className="group mb-1 hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-coral sm:flex">
          View All Writing <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
        </Link>
      </div>

      {visible.length === 0 ? (
        <EmptyState type="writing" to="/app/studio" compact className="mt-2" />
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {visible.map((story) => <StoryCard key={story.id} story={story} />)}
        </motion.div>
      )}

      <Link to="/app/studio" className="group mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-coral sm:hidden">
        View All Writing <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
      </Link>
    </section>
  );
}
